import React, { useContext, useEffect, useRef, useState } from 'react';

import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import LazyLoading from '../../../components/LazyLoading';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';

import { Society } from '../../../app/models/Society';
import { BusinessName } from '../../../app/models/BusinessName';
import usePurchaseOrderService from '../../../app/services/hooks/usePurchaseOrderService';
import useBusinessNameContactService from '../../../app/services/hooks/useBusinessNameContactService';
import { PurchaseorderDataForm, defaultPurchaseOrderDataForm } from '../../../app/models/PurchaseOrder';
import { Incoterm } from '../../../app/models/Incoterm';
import PurchaseOrderForm from './forms/PurchaseOrderForm';
import useSocietyService from '../../../app/services/hooks/useSocietyService';
import useProviderService from '../../../app/services/hooks/useProviderService';
import { PaymentCondition } from '../../../app/models/PaymentCondition';
import useNavigationPage from '../../../hooks/useNavigationPage';
import { Place } from '../../../app/models/Place';
import { Currency } from '../../../app/models/Currency';
import { PaymentMethod } from '../../../app/models/PaymentMethod';
import { ProviderContact } from '../../../app/models/ProviderContact';
import { Location } from '../../../app/models/Location';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const PurchaseOrderCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();

    const {
        createPurchaseOrder, 
        storePurchaseOrder, 
        fetchingCreatePurchaseOrder, 
        fetchingStorePurchaseOrder 
    } = usePurchaseOrderService();

    const {
        getSociety,
        fetchingGetSociety,
        getSocietyAndProviders,
        fetchingGetSocietyAndProviders
    } = useSocietyService();

    const {
        getBusinessNameContactsByBusinessName,
        fetchingGetBusinessNameContactsByBusinessName
    } = useBusinessNameContactService();

    const [societies, setSocieties] = useState<Society[]>([]);
    const [providerBusinessNames, setProviderBusinessNames] = useState<BusinessName[]>([]);
    const [providerContacts, setProviderContacts] = useState<ProviderContact[]>([]);
    const [incoterms, setincoterms] = useState<Incoterm[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [paymentConditions, setPaymentConditions] = useState<PaymentCondition[]>([]);
    const [purchaseOrder, setPurchaseOrder] = useState<PurchaseorderDataForm>(defaultPurchaseOrderDataForm);
    const [places, setPlaces] = useState<Place[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [errorFields, setErrorFields] = useState<any>();
    
    const providerContactIdSelected = useRef<number | undefined>(0);
    const providerBusinessNameIdSelected = useRef<number | undefined>(0);
    const societyIdSelected = useRef<number | undefined>(0);

    useEffect(() => {
        if (showLoading) showLoading('loading', 'Cargando datos...');

        createPurchaseOrder({
            onSuccess: (response: ServiceResponse) => {
                setSocieties(response.data.societies);
                setProviderBusinessNames(response.data.provider_business_names || []);
                setincoterms(response.data.incoterms);
                setCurrencies(response.data.currencies);
                setPaymentConditions(response.data.payment_conditions);
                setPlaces(response.data.places);
                setLocations(response.data.locations || []);
                setPaymentMethods(response.data.payment_methods)
                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        });
    }, []);

    useEffect(() => {
        if (purchaseOrder.society_id == undefined) {
            societyIdSelected.current = undefined;
        }

        if (purchaseOrder.society_id && purchaseOrder.society_id != societyIdSelected.current) {
            getSocietyAndProviders(purchaseOrder.society_id, {
                onSuccess: (response: ServiceResponse) => {
                    societyIdSelected.current = purchaseOrder.society_id ?? 0;
                    setErrorFields(undefined);

                    setProviderBusinessNames(response.data.provider_business_names || []);
                    setProviderContacts([])
                    setPurchaseOrder({
                        ...purchaseOrder,
                        society_rut: response.data.society.rut,
                        society_name: response.data.society.business_name,
                        society_address: response.data.society.address,
                        provider_business_name_id: 0,
                        provider_contact_id: 0,
                        provider_rut: '',
                        provider_name: '',
                        provider_address: '',
                        provider_name_contact: '',
                        provider_email_contact: '',
                        provider_phone_contact: ''
                    });
                },
                onError: (response: ServiceResponse) => {
                    setPurchaseOrder({
                        ...purchaseOrder,
                        society_id: societyIdSelected.current
                    });
                }
            })
        }
    }, [purchaseOrder.society_id]);

    useEffect(() => {
        if (purchaseOrder.provider_business_name_id == undefined) {
            providerBusinessNameIdSelected.current = undefined;
        }
        if (purchaseOrder.provider_business_name_id && purchaseOrder.provider_business_name_id != providerBusinessNameIdSelected.current) {
            const selectedBusinessName = providerBusinessNames.find(bn => bn.id === purchaseOrder.provider_business_name_id);
            
            if (selectedBusinessName) {
                providerBusinessNameIdSelected.current = purchaseOrder.provider_business_name_id ?? 0;
                setErrorFields(undefined);
                
                // Cargar contactos de la razón social
                getBusinessNameContactsByBusinessName(purchaseOrder.provider_business_name_id, undefined, {
                    onSuccess: (response: ServiceResponse) => {
                        setProviderContacts(response.data.provider_contacts || []);
                    },
                    onError: (response: ServiceResponse) => {
                        setProviderContacts([]);
                    }
                });
                
                setPurchaseOrder({
                    ...purchaseOrder,
                    provider_rut: selectedBusinessName.rut || '',
                    provider_name: selectedBusinessName.name || '',
                    provider_address: selectedBusinessName.address || '',
                    provider_contact_id: 0,
                    provider_name_contact: '',
                    provider_email_contact: '',
                    provider_phone_contact: ''
                });
            } else {
                setPurchaseOrder({
                    ...purchaseOrder,
                    provider_business_name_id: providerBusinessNameIdSelected.current
                });
            }
        }
    }, [purchaseOrder.provider_business_name_id, providerBusinessNames]);

    useEffect(() => {
        if(purchaseOrder.provider_contact_id){
            const providerContact = providerContacts.find(item => item.id === purchaseOrder.provider_contact_id)

            if(providerContact){
                setPurchaseOrder({
                    ...purchaseOrder,
                    provider_name_contact: providerContact.name,
                    provider_email_contact: providerContact.email,
                    provider_phone_contact: `${providerContact.phone_code ?? ""}-${providerContact.phone ?? ""}`
                })
            }
        }
    }, [purchaseOrder.provider_contact_id]);

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando tracto...');
        storePurchaseOrder(purchaseOrder, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                
                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
                
                toast.success(response.message, {
                    autoClose: 2500
                });
                setPurchaseOrder(defaultPurchaseOrderDataForm);
                navigationPage('/purchase-orders/' + response.data.purchase_order_id + '/edit')
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return fetchingCreatePurchaseOrder ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <div className="row">
                <div className="col-12">
                    <PurchaseOrderForm
                        purchaseOrderDataForm={purchaseOrder}
                        setPurchaseOrderDataForm={setPurchaseOrder}
                        societies={societies}
                        providerBusinessNames={providerBusinessNames}
                        providerContacts={providerContacts}
                        incoterms={incoterms}
                        paymentMethods={paymentMethods}
                        currencies={currencies}
                        paymentConditions={paymentConditions}
                        errorFields={errorFields}
                        societyLoading={fetchingGetSocietyAndProviders}
                        providerLoading={fetchingGetBusinessNameContactsByBusinessName}
                        providerContactLoading={fetchingGetBusinessNameContactsByBusinessName}
                        places={places}
                        locations={locations}
                    />
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} locked={fetchingStorePurchaseOrder} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStorePurchaseOrder} />
                </div>
            </div>
        </>
    );
};

export default PurchaseOrderCreate;